/**
 * "Open in Google Docs": uploads the editor's .docx (see word-export.ts) to the
 * user's own Google Drive, where Drive converts it into a native Google Doc.
 *
 * Authorisation uses Google Identity Services' token model in the browser with
 * the narrow `drive.file` scope — the app can only see and change files it
 * created itself, never the rest of the user's Drive. The access token is kept
 * in memory only (never stored) and expires after about an hour.
 *
 * Kept free of heavy imports so it ships in the editor bundle: the consent
 * popup has to be opened straight from the click, before any lazy chunk loads,
 * or the browser's popup blocker would stop it.
 */

const GIS_SRC = "https://accounts.google.com/gsi/client";
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const GOOGLE_DOC_MIME = "application/vnd.google-apps.document";
const UPLOAD_URL = "https://www.googleapis.com/upload/drive/v3/files";
const ABOUT_URL = "https://www.googleapis.com/drive/v3/about?fields=kind";
/** Browser-local memory that the Drive API isn't enabled for this app yet. */
const UNAVAILABLE_KEY = "pb.googleDocs.unavailableAt";
/** How long "coming soon" sticks before the next click may check again. Only a
 *  signed-in request can tell whether the API is on, so it can't be polled. */
const RECHECK_AFTER_MS = 24 * 60 * 60 * 1000;
/** Drive's multipart upload limit; bigger files go through a resumable upload. */
const MULTIPART_LIMIT = 5 * 1024 * 1024;

export type GoogleDocsErrorCode =
  | "cancelled"
  | "not_granted"
  | "popup_blocked"
  | "unavailable"
  | "api_disabled"
  | "unauthorized"
  | "upload_failed";

export class GoogleDocsError extends Error {
  constructor(readonly code: GoogleDocsErrorCode, message: string) {
    super(message);
  }
}

interface TokenResponse {
  access_token?: string;
  expires_in?: number | string;
  error?: string;
  error_description?: string;
}

interface GoogleOAuth2 {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }): { requestAccessToken(overrides?: { prompt?: string }): void };
  hasGrantedAllScopes(response: TokenResponse, scope: string): boolean;
}

let cachedToken: { value: string; expiresAt: number } | null = null;
let gisLoading: Promise<void> | null = null;

function oauth2(): GoogleOAuth2 | null {
  return (window as unknown as { google?: { accounts?: { oauth2?: GoogleOAuth2 } } })
    .google?.accounts?.oauth2 || null;
}

/** Load Google Identity Services (usually already loaded by Google sign-in). */
export function preloadGoogleIdentity(): Promise<void> {
  if (oauth2()) {
    return Promise.resolve();
  }
  if (!gisLoading) {
    gisLoading = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(`script[src="${GIS_SRC}"]`) as HTMLScriptElement | null;
      const script = existing || document.createElement("script");
      const done = () => (oauth2() ? resolve() : reject(new Error("GIS unavailable")));
      setTimeout(() => reject(new Error("GIS load timed out")), 10_000);
      script.addEventListener("load", done, { once: true });
      script.addEventListener("error", () => reject(new Error("GIS failed to load")), { once: true });
      if (!existing) {
        script.src = GIS_SRC;
        script.async = true;
        document.head.appendChild(script);
      } else if (oauth2()) {
        resolve();
      }
    }).catch((error) => {
      gisLoading = null; // allow a retry on the next click
      throw error;
    });
  }
  return gisLoading;
}

/** A still-valid token from earlier in this session, if any. */
export function getCachedGoogleToken(): string | null {
  if (cachedToken && cachedToken.expiresAt - 60_000 > Date.now()) {
    return cachedToken.value;
  }
  cachedToken = null;
  return null;
}

/**
 * Ask Google for a Drive token. Call it directly from the click handler: it
 * opens Google's consent/account popup, which browsers only allow right after
 * a user gesture.
 */
export async function requestGoogleToken(clientId: string): Promise<string> {
  if (!oauth2()) {
    try {
      await preloadGoogleIdentity();
    } catch {
      throw new GoogleDocsError("unavailable", "Couldn't reach Google. Check your connection and try again.");
    }
  }
  const api = oauth2()!;
  return new Promise<string>((resolve, reject) => {
    const client = api.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(
            response.error === "access_denied"
              ? new GoogleDocsError("not_granted", "Google Drive access was not allowed.")
              : new GoogleDocsError("unauthorized", response.error_description || "Google sign-in failed.")
          );
          return;
        }
        if (!api.hasGrantedAllScopes(response, DRIVE_SCOPE)) {
          reject(
            new GoogleDocsError(
              "not_granted",
              "Allow \"See, edit, create and delete only the specific Google Drive files you use with this app\" to open the document in Google Docs."
            )
          );
          return;
        }
        cachedToken = {
          value: response.access_token,
          expiresAt: Date.now() + (Number(response.expires_in) || 3600) * 1000,
        };
        resolve(response.access_token);
      },
      error_callback: (error) => {
        reject(
          error?.type === "popup_failed_to_open"
            ? new GoogleDocsError("popup_blocked", "The Google sign-in window was blocked. Allow pop-ups for this site and try again.")
            : new GoogleDocsError("cancelled", "Google sign-in was closed.")
        );
      },
    });
    client.requestAccessToken();
  });
}

/**
 * True while an earlier attempt found the Drive API disabled for this app
 * (within RECHECK_AFTER_MS), so the button can show "coming soon" up front.
 */
export function isGoogleDocsMarkedUnavailable(): boolean {
  try {
    const at = Number(localStorage.getItem(UNAVAILABLE_KEY));
    if (at && Date.now() - at < RECHECK_AFTER_MS) {
      return true;
    }
    localStorage.removeItem(UNAVAILABLE_KEY);
  } catch {
    // storage blocked (private mode etc.) → just try each time
  }
  return false;
}

/** Remember (or forget) that the Drive API is disabled for this app. */
export function setGoogleDocsUnavailable(unavailable: boolean): void {
  try {
    if (unavailable) {
      localStorage.setItem(UNAVAILABLE_KEY, String(Date.now()));
    } else {
      localStorage.removeItem(UNAVAILABLE_KEY);
    }
  } catch {
    // storage blocked → nothing to remember
  }
}

/**
 * Cheap signed-in Drive call made before converting anything, so a disabled
 * Drive API (GoogleDocsError "api_disabled") is found in one quick request.
 */
export async function checkGoogleDriveAvailable(token: string, signal?: AbortSignal): Promise<void> {
  let response: Response;
  try {
    response = await fetch(ABOUT_URL, { headers: { Authorization: `Bearer ${token}` }, signal });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw error;
    }
    throw new GoogleDocsError("upload_failed", "Couldn't reach Google Drive. Check your connection and try again.");
  }
  if (!response.ok) {
    throw await driveError(response);
  }
}

/**
 * Upload a .docx to the user's Drive as a Google Doc (Drive converts it on
 * import) and return the new document's editor URL.
 */
export async function uploadDocxAsGoogleDoc(
  token: string,
  docx: Blob,
  name: string,
  signal?: AbortSignal
): Promise<string> {
  const metadata = JSON.stringify({ name, mimeType: GOOGLE_DOC_MIME });
  const auth = { Authorization: `Bearer ${token}` };
  let response: Response;

  try {
    if (docx.size <= MULTIPART_LIMIT) {
      const boundary = `pb-${Math.random().toString(36).slice(2)}`;
      const body = new Blob([
        `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${metadata}\r\n`,
        `--${boundary}\r\nContent-Type: ${DOCX_MIME}\r\n\r\n`,
        docx,
        `\r\n--${boundary}--`,
      ]);
      response = await fetch(`${UPLOAD_URL}?uploadType=multipart&fields=id,webViewLink`, {
        method: "POST",
        headers: { ...auth, "Content-Type": `multipart/related; boundary=${boundary}` },
        body,
        signal,
      });
    } else {
      const session = await fetch(`${UPLOAD_URL}?uploadType=resumable&fields=id,webViewLink`, {
        method: "POST",
        headers: {
          ...auth,
          "Content-Type": "application/json; charset=UTF-8",
          "X-Upload-Content-Type": DOCX_MIME,
          "X-Upload-Content-Length": String(docx.size),
        },
        body: metadata,
        signal,
      });
      const location = session.ok ? session.headers.get("Location") : null;
      if (!location) {
        throw await driveError(session);
      }
      response = await fetch(location, {
        method: "PUT",
        headers: { "Content-Type": DOCX_MIME },
        body: docx,
        signal,
      });
    }
  } catch (error) {
    if (error instanceof GoogleDocsError || (error as Error)?.name === "AbortError") {
      throw error;
    }
    throw new GoogleDocsError("upload_failed", "Couldn't upload to Google Drive. Check your connection and try again.");
  }

  if (!response.ok) {
    throw await driveError(response);
  }
  const file = (await response.json()) as { id?: string; webViewLink?: string };
  if (!file.id) {
    throw new GoogleDocsError("upload_failed", "Google Drive didn't return the new document.");
  }
  return file.webViewLink || `https://docs.google.com/document/d/${file.id}/edit`;
}

/** Map a Drive API error response to a message a user (or admin) can act on. */
async function driveError(response: Response): Promise<GoogleDocsError> {
  let reason = "";
  let message = "";
  try {
    const body = (await response.json()) as {
      error?: { message?: string; errors?: Array<{ reason?: string }>; details?: Array<{ reason?: string }> };
    };
    reason = body.error?.errors?.[0]?.reason || body.error?.details?.[0]?.reason || "";
    message = body.error?.message || "";
  } catch {
    // non-JSON error body
  }
  if (response.status === 401) {
    cachedToken = null;
    return new GoogleDocsError("unauthorized", "Your Google session expired. Try again.");
  }
  if (/accessNotConfigured|SERVICE_DISABLED/i.test(reason + message)) {
    return new GoogleDocsError(
      "api_disabled",
      "The Google Drive API isn't enabled for this app yet. Ask your administrator to enable it in Google Cloud."
    );
  }
  if (response.status === 403 && /insufficient/i.test(reason + message)) {
    cachedToken = null;
    return new GoogleDocsError("not_granted", "Google Drive access was not allowed.");
  }
  return new GoogleDocsError(
    "upload_failed",
    `Google Drive rejected the upload${message ? `: ${message}` : ` (HTTP ${response.status})`}.`
  );
}
